'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface HabitEntry {
  id: string;
  label: string;
  description: string;
  icon: string;
  category: 'reduce' | 'save' | 'grow';
}

const habits: HabitEntry[] = [
  { id: 'habit-plantable-paper', label: 'Plantable Paper & Print Reduction', description: 'Use plantable papers for visiting cards; print only when necessary; avoid tissues', icon: 'DocumentTextIcon', category: 'reduce' },
  { id: 'habit-carry-bag', label: 'Carry Own Bags & Bottle', description: 'Carry reusable bags for shopping; carry water bottle to avoid plastic/disposable cups', icon: 'ShoppingBagIcon', category: 'reduce' },
  { id: 'habit-vegetarian', label: 'Vegetarian & Satvik Diet', description: 'Follow and encourage vegetarian diet; Satvik Rasoi workshops; mindful eating to avoid food waste; prefer organic', icon: 'HeartIcon', category: 'save' },
  { id: 'habit-carpool', label: 'Car Share / Carpool', description: 'Start and encourage carpooling to reduce individual vehicle use and emissions', icon: 'TruckIcon', category: 'save' },
  { id: 'habit-waste-disposal', label: 'Proper Waste Disposal', description: 'Adopt correct habits for dry waste, wet waste, e-waste, and recyclable waste segregation', icon: 'TrashIcon', category: 'reduce' },
  { id: 'habit-simple-living', label: 'Simple Living & Sharing', description: 'Live simply; avoid single-use; share second-hand things; repair instead of replacing', icon: 'GiftIcon', category: 'reduce' },
  { id: 'habit-led-energy', label: 'LED Lighting & Energy Saving', description: 'Switch to LED; turn off lights, appliances, and electronics when not in use', icon: 'BoltIcon', category: 'save' },
  { id: 'habit-water-saving', label: 'Water Conservation Habits', description: 'Shorter showers; turn off taps while brushing, soaping; report leaks promptly', icon: 'BeakerIcon', category: 'save' },
  { id: 'habit-compost-garden', label: 'Composting & Kitchen Garden', description: 'Start a compost or kitchen garden to use organic waste at home productively', icon: 'GlobeAltIcon', category: 'grow' },
];

const adoptionLevels = [
  { value: 'none', label: 'Not Started', color: 'bg-muted text-muted-foreground', bar: 0 },
  { value: 'low', label: 'A Few Members', color: 'bg-[var(--warning-bg)] text-[var(--warning)]', bar: 25 },
  { value: 'moderate', label: 'About Half', color: 'bg-[var(--info-bg)] text-[var(--info)]', bar: 50 },
  { value: 'high', label: 'Most Members', color: 'bg-[var(--success-bg)] text-[var(--success)]', bar: 75 },
  { value: 'full', label: 'All Members', color: 'bg-primary/10 text-primary', bar: 100 },
];

const categoryConfig = {
  reduce: { label: 'Reduce', color: 'text-[var(--danger)]', bg: 'bg-[var(--danger-bg)]' },
  save: { label: 'Save', color: 'text-[var(--info)]', bg: 'bg-[var(--info-bg)]' },
  grow: { label: 'Grow', color: 'text-[var(--success)]', bg: 'bg-[var(--success-bg)]' },
};

interface HabitsTabProps {
  onChange: () => void;
  onScoreChange?: (score: number) => void;
}

export default function HabitsTab({ onChange, onScoreChange }: HabitsTabProps) {
  const [adoptionMap, setAdoptionMap] = useState<Record<string, string>>(() =>
    habits.reduce((acc, h) => ({ ...acc, [h.id]: 'none' }), {})
  );
  const [notesMap, setNotesMap] = useState<Record<string, string>>({});
  const [expandedHabit, setExpandedHabit] = useState<string | null>(null);

  const setAdoption = (id: string, value: string) => {
    setAdoptionMap((prev) => {
      const next = { ...prev, [id]: value };
      const score = Math.round(
        habits.reduce((sum, h) => {
          const level = adoptionLevels.find((l) => l.value === next[h.id]);
          return sum + (level?.bar ?? 0);
        }, 0) / habits.length
      );
      onScoreChange?.(score);
      return next;
    });
    onChange();
  };

  const setNote = (id: string, value: string) => {
    setNotesMap((prev) => ({ ...prev, [id]: value }));
    onChange();
  };

  const overallScore = Math.round(
    habits.reduce((sum, h) => {
      const level = adoptionLevels.find((l) => l.value === adoptionMap[h.id]);
      return sum + (level?.bar ?? 0);
    }, 0) / habits.length
  );

  return (
    <div className="space-y-4">
      {/* Overall score */}
      <div className="bg-card rounded-2xl border border-border shadow-card p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="text-sm font-bold text-foreground">Habit Adoption Score</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Composite across all 9 tracked eco-habits</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-3xl font-extrabold text-primary tabular-nums">{overallScore}%</span>
          </div>
        </div>
        <div className="w-full bg-muted rounded-full h-2.5">
          <div
            className="h-2.5 rounded-full bg-primary transition-all duration-500"
            style={{ width: `${overallScore}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-muted-foreground">0% — Not Started</span>
          <span className="text-xs text-muted-foreground">100% — All Members</span>
        </div>

        {/* Category summary */}
        <div className="grid grid-cols-3 gap-3 mt-4">
          {(['reduce', 'save', 'grow'] as const).map((cat) => {
            const catHabits = habits.filter((h) => h.category === cat);
            const catScore = Math.round(
              catHabits.reduce((sum, h) => {
                const level = adoptionLevels.find((l) => l.value === adoptionMap[h.id]);
                return sum + (level?.bar ?? 0);
              }, 0) / catHabits.length
            );
            const conf = categoryConfig[cat];
            return (
              <div key={`cat-${cat}`} className={`rounded-xl p-3 ${conf.bg}`}>
                <p className={`text-[10px] font-bold uppercase tracking-wider ${conf.color}`}>{conf.label}</p>
                <p className={`text-xl font-extrabold tabular-nums mt-1 ${conf.color}`}>{catScore}%</p>
                <p className="text-[10px] text-muted-foreground">{catHabits.length} habits</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Individual habits */}
      <div className="space-y-3">
        {habits.map((habit, idx) => {
          const currentLevel = adoptionLevels.find((l) => l.value === adoptionMap[habit.id]) ?? adoptionLevels[0];
          const isExpanded = expandedHabit === habit.id;
          const catConf = categoryConfig[habit.category];

          return (
            <div
              key={habit.id}
              className="bg-card rounded-2xl border border-border shadow-card overflow-hidden transition-all duration-200"
            >
              <div
                className="flex items-start gap-3 p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                onClick={() => setExpandedHabit(isExpanded ? null : habit.id)}
              >
                <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon name={habit.icon as any} size={16} variant="outline" className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-foreground">{idx + 1}. {habit.label}</span>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md ${catConf.bg} ${catConf.color}`}>
                        {catConf.label}
                      </span>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${currentLevel.color}`}>
                      {currentLevel.label}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{habit.description}</p>

                  {/* Mini progress bar */}
                  <div className="mt-2 w-full bg-muted rounded-full h-1.5">
                    <div
                      className="h-1.5 rounded-full bg-primary transition-all duration-300"
                      style={{ width: `${currentLevel.bar}%` }}
                    />
                  </div>
                </div>
                <Icon
                  name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={16}
                  variant="outline"
                  className="text-muted-foreground flex-shrink-0 mt-1"
                />
              </div>

              {isExpanded && (
                <div className="px-4 pb-4 pt-0 space-y-4 border-t border-border animate-fade-in">
                  <div className="pt-4">
                    <label className="form-label" htmlFor={`adoption-${habit.id}`}>
                      Adoption Level at Centre
                    </label>
                    <p className="form-helper">How many members at your centre are practising this habit regularly?</p>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-2">
                      {adoptionLevels.map((level) => (
                        <button
                          key={`level-${habit.id}-${level.value}`}
                          type="button"
                          onClick={() => setAdoption(habit.id, level.value)}
                          className={`px-3 py-2 rounded-xl text-xs font-semibold border transition-all duration-150 ${
                            adoptionMap[habit.id] === level.value
                              ? 'border-primary bg-primary text-white shadow-sm'
                              : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground'
                          }`}
                        >
                          {level.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="form-label" htmlFor={`note-${habit.id}`}>
                      Observations & Notes
                    </label>
                    <textarea
                      id={`note-${habit.id}`}
                      rows={2}
                      placeholder="Specific examples, challenges faced, success stories from centre members..."
                      className="form-input resize-none"
                      value={notesMap[habit.id] ?? ''}
                      onChange={(e) => setNote(habit.id, e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}